import { useState } from 'react';
import { uploadTempImage } from '../services/storage.service.v2';
import { createPendingPattern } from '../services/pendingPattern.service';
import { createPatternCheckoutSession } from '../services/purchase.service';
import {
	trackError,
	trackPurchaseEvent,
	ANALYTICS_EVENTS,
	ERROR_CONTEXTS,
} from '../services/analytics.service';

/**
 * Random ID generator for pattern IDs
 */
const generatePatternId = () => {
	const chars =
		'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	let result = '';
	for (let i = 0; i < 32; i++) {
		result += chars.charAt(Math.floor(Math.random() * chars.length));
	}
	return result;
};

/**
 * Hook for orchestrating the purchase flow
 * Handles: image upload → pending pattern creation → checkout session → redirect to Stripe
 *
 * @param {object} user - Firebase user object
 * @returns {object}
 *   - purchasePattern: function to initiate purchase
 *   - purchasing: boolean indicating purchase in progress
 *   - error: error message if purchase failed
 */
export const usePurchasePattern = (user) => {
	const [purchasing, setPurchasing] = useState(false);
	const [error, setError] = useState(null);

	const purchasePattern = async ({
		type, // 'library' | 'user'
		fileName, // library primary image base name
		fileName2, // library secondary image base name (lenticular)
		imagePrimaryDataURL, // user-uploaded primary image data URL
		imageSecondaryDataURL, // user-uploaded secondary image data URL (lenticular)
		bookParams, // { height, lastOddPage, angleLine, isInkSaver, measurementUnit, extraFrontPages, extraBackPages }
		priceId, // Stripe price ID
		productId, // Stripe product ID
	}) => {
		if (!user || !user.uid) {
			const err = new Error('User must be authenticated');
			setError(err.message);
			throw err;
		}

		setPurchasing(true);
		setError(null);

		try {
			// Step 1: Generate pattern ID
			const patternId = generatePatternId();

			// Step 2: Upload images if user-uploaded pattern
			let tempImagePath = null;
			let tempImagePath2 = null;

			if (type === 'user') {
				const primaryUpload = await uploadTempImage(
					user.uid,
					patternId,
					imagePrimaryDataURL,
				);
				tempImagePath = primaryUpload.path;

				if (imageSecondaryDataURL) {
					const secondaryUpload = await uploadTempImage(
						user.uid,
						patternId,
						imageSecondaryDataURL,
						'-2',
					);
					tempImagePath2 = secondaryUpload.path;
				}
			}

			// Step 3: Create pending pattern document
			await createPendingPattern(user.uid, patternId, {
				type,
				fileName: fileName || null,
				fileName2: fileName2 || null,
				tempImagePath,
				tempImagePath2,
				bookParams,
				priceId,
				productId,
			});

			// Step 4: Create checkout session and get Stripe URL
			const checkoutUrl = await createPatternCheckoutSession(user.uid, {
				pendingPatternId: patternId,
				priceId,
			});

			// Step 5: Redirect to Stripe
			trackPurchaseEvent(ANALYTICS_EVENTS.STRIPE_REDIRECT_INITIATED);
			window.location.assign(checkoutUrl);
		} catch (err) {
			trackError(ERROR_CONTEXTS.PURCHASE_PATTERN, err);
			setError(err.message || 'Failed to initiate purchase');
			setPurchasing(false);
			throw err;
		}
	};

	return {
		purchasePattern,
		purchasing,
		error,
	};
};
